/* global HTMLTextAreaElement, URL */

import { Button, makeStyles, Textarea, tokens } from "@fluentui/react-components";
import { DismissCircle16Filled, Send16Regular, SpinnerIos16Regular } from "@fluentui/react-icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtom, useStore } from "jotai";
import * as React from "react";
import { createChatMessage, createChatThread } from "@/api/mutations";
import { spinAnimation } from "@/utils/animations";
import { chatThreadIdAtom, chatSessionAtom, messageHistoryAtom } from "@/state";
import type { ChatContext } from "@/utils/chat-selection";
import type { ChatMessage } from "@/types";
import { getHighlightedText } from "@/utils/editor-actions";
import { blobToBase64 } from "@/utils/helpers";
import { withReportCitationLinks } from "@/utils/report-citations";

const MAX_IMAGES = 1;

export default function ChatInput({
  context,
  organizationId,
}: {
  context: ChatContext | null;
  organizationId: string;
}) {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const store = useStore();
  const submitting = React.useRef(false);
  const [error, setError] = React.useState("");

  const [imageSources, setImageSources] = React.useState<string[]>([]);
  const [imageData, setImageData] = React.useState<string[]>([]);

  const [newMessage, setNewMessage] = React.useState("");

  const [messageHistory, setMessageHistory] = useAtom(messageHistoryAtom);

  const { mutate, isPending } = useMutation({
    mutationFn: async (variables: {
      context: ChatContext;
      message: string;
      images: string[];
      session: number;
      history: ChatMessage[];
    }) => {
      if (store.get(chatSessionAtom) !== variables.session) return null;
      let threadId = store.get(chatThreadIdAtom);
      if (!threadId) {
        const thread = await createChatThread({
          organizationId,
          context: variables.context,
          messageHistory: variables.history,
        });
        if (store.get(chatSessionAtom) !== variables.session) return null;
        threadId = thread.threadId;
        store.set(chatThreadIdAtom, threadId);
      }
      return createChatMessage({
        organizationId,
        context: variables.context,
        threadId,
        message: variables.message,
        images: variables.images,
        isNewThread: variables.history.length === 0,
      });
    },
    onMutate: (variables) => {
      if (store.get(chatSessionAtom) !== variables.session) return;
      setMessageHistory((prev) => [
        ...prev,
        {
          role: "user",
          content: {
            text: variables.message,
            includesAttachments: variables.images.length > 0,
          },
        },
      ]);
      setError("");
    },
    onSuccess: (data, variables) => {
      if (!data || store.get(chatSessionAtom) !== variables.session) return;
      queryClient.invalidateQueries({ queryKey: ["queries"] });
      const messages = data.messages.map((message) => {
        if (message.role !== "assistant" || !message.content.citations.reportCitations?.length) return message;
        return {
          ...message,
          content: {
            ...message.content,
            citations: {
              ...message.content.citations,
              reportCitations: withReportCitationLinks(message.content.citations.reportCitations, {
                organizationId,
                context: variables.context,
              }),
            },
          },
        };
      });
      setMessageHistory((prev) => [...prev, ...messages]);
      setNewMessage("");
      setImageData([]);
      setImageSources([]);
    },
    onError: (cause, variables) => {
      if (store.get(chatSessionAtom) !== variables.session) return;
      setMessageHistory((prev) => prev.slice(0, -1));
      setNewMessage(variables.message);
      setError(cause instanceof Error ? cause.message : "Could not send your message. Please try again.");
    },
    onSettled: () => {
      submitting.current = false;
    },
  });

  React.useEffect(() => {
    return () => {
      imageSources.forEach(URL.revokeObjectURL);
    };
  }, [imageSources]);

  const handlePaste = async (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!context || submitting.current) return;
    const items = event.clipboardData?.items;

    if (!items) return;

    let imageUrls: string[] = [];
    let base64Data: string[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      if (item.type.indexOf("image") !== -1) {
        event.preventDefault();

        if (imageSources.length + imageUrls.length >= MAX_IMAGES) break;

        const blob = item.getAsFile();

        if (blob) {
          const url = URL.createObjectURL(blob);
          imageUrls.push(url);
          const base64 = await blobToBase64(blob);
          base64Data.push(base64);
        }
      }
    }
    if (imageUrls.length > 0) {
      setImageSources((prev) => [...prev, ...imageUrls]);
      setImageData((prev) => [...prev, ...base64Data]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!context || !organizationId || submitting.current) return;
    submitting.current = true;
    const session = store.get(chatSessionAtom);
    try {
      const textInput =
        newMessage.trim() ||
        (context.requestType === "performance" && imageData.length ? "" : (await getHighlightedText()).trim());
      if (store.get(chatSessionAtom) !== session || (!textInput && imageData.length === 0)) {
        submitting.current = false;
        return;
      }
      mutate({ context, message: textInput, images: imageData, session, history: messageHistory });
    } catch {
      submitting.current = false;
      setError("Could not read the selection. Type your question and try again.");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleDeleteImage = (src: string) => {
    const index = imageSources.indexOf(src);
    setImageData((prev) => prev.filter((_, i) => i !== index));
    setImageSources((prev) => prev.filter((url) => url !== src));
  };

  return (
    <section className={styles.container}>
      {error && <p role="alert">{error}</p>}
      {imageSources.length >= 1 && (
        <div className={styles.thumbnail_image__container}>
          {imageSources.map((src, i) => {
            return (
              <div key={`${i}`} className={styles.thumbnail_image__wrapper}>
                <img className={styles.thumbnail_image} src={src} alt="Pasted" />
                <button
                  className={styles.delete_button}
                  onClick={() => handleDeleteImage(src)}
                  aria-label="Delete Image"
                  disabled={isPending}
                >
                  <DismissCircle16Filled />
                </button>
              </div>
            );
          })}
        </div>
      )}
      <form onSubmit={handleSubmit} className={styles.form}>
        <Textarea
          placeholder="Enter or highlight a question, or simply paste in an image."
          className={styles.input}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste as any}
          disabled={!context || isPending}
          aria-label="Message"
        />
        <Button type="submit" aria-label="Send message" className={styles.button} disabled={isPending || !context}>
          <div className={styles.button__content}>
            {isPending ? <SpinnerIos16Regular className={styles.loading_spinner} /> : <Send16Regular />}
          </div>
        </Button>
      </form>
    </section>
  );
}

const useStyles = makeStyles({
  container: {
    paddingBottom: "8px",
    paddingTop: "8px",
  },
  form: {
    display: "grid",
    height: "100px",
    gap: "8px",
    gridTemplateColumns: "minmax(0, 1fr) 40px",
  },
  input: {
    minWidth: 0,
    borderRadius: "8px",
  },
  button: {
    minWidth: "40px",
    color: "#fff",
    backgroundColor: tokens.colorPaletteLavenderForeground2,
    ["&:hover"]: {
      backgroundColor: tokens.colorPaletteLavenderBackground2,
    },
    cursor: "pointer",
    ["&:disabled"]: {
      backgroundColor: tokens.colorPaletteLavenderBackground2,
      cursor: "not-allowed",
    },
    borderRadius: "8px",
  },
  button__content: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
    alignContent: "center",
  },
  loading_spinner: {
    animationName: spinAnimation,
    animationDuration: "1.5s",
    animationIterationCount: "infinite",
    animationTimingFunction: "linear",
  },
  thumbnail_image__container: {
    display: "flex",
    gap: "8px",
    position: "relative",
    alignItems: "center",
    borderRadius: "8px",
    overflowX: "auto",
    overflowY: "hidden",
    border: "1px solid #ccc",
    marginBottom: "8px",
    marginTop: "-32px",
    backgroundColor: "#f9f9f9",
    padding: "8px",
    height: "64px",
  },
  thumbnail_image__wrapper: {
    position: "relative",
    display: "inline-block",
  },
  thumbnail_image: {
    maxWidth: "100%",
    maxHeight: "64px",
    borderRadius: "4px",
  },
  delete_button: {
    position: "absolute",
    top: "-6px",
    right: "-6px",
    color: "#666",
    border: "none",
    background: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
    margin: 0,
  },
});
